/**
 * Task #2509 — Hook condiviso per il catalogo globale forme giuridiche
 * (`/api/catalog/legal-forms`). Fonte di verità unica: tabella
 * `public.legal_forms`. Sostituisce le liste hardcoded divergenti su FE.
 *
 * Stand-alone (no react-query): @easyflow/ui-frontend non dichiara
 * @tanstack/react-query. useState/useEffect + cache TTL in-memory cross-componenti.
 * Endpoint JWT-only (sia tenant sia master). Token multi-chiave da localStorage.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface LegalFormOption {
  code: string;
  label: string;
  description: string | null;
  category: string;
  sort_order: number;
  is_active: boolean;
}

export interface UseLegalFormsResult {
  legalForms: LegalFormOption[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

interface CacheEntry {
  value: LegalFormOption[];
  expires: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_KEY = 'legal-forms';
const cache = new Map<string, CacheEntry>();

function readToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return (
    localStorage.getItem('edgvoip_master_token') ||
    localStorage.getItem('edgvoip_tenant_token') ||
    localStorage.getItem('edgvoip_impersonation_token') ||
    localStorage.getItem('token')
  );
}

interface LegalFormsApiResponse {
  data?: { legal_forms?: LegalFormOption[] };
  legal_forms?: LegalFormOption[];
}

export function useLegalForms(enabled = true): UseLegalFormsResult {
  const [legalForms, setLegalForms] = useState<LegalFormOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef<boolean>(true);

  const doFetch = useCallback(async (force: boolean): Promise<void> => {
    if (!force) {
      const cached = cache.get(CACHE_KEY);
      if (cached && cached.expires > Date.now()) {
        if (mountedRef.current) {
          setLegalForms(cached.value);
          setError(null);
        }
        return;
      }
    }
    if (mountedRef.current) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const token = readToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch('/api/catalog/legal-forms', { headers });
      if (!res.ok) {
        throw new Error(`Legal forms fetch failed: HTTP ${res.status}`);
      }
      const body = (await res.json()) as LegalFormsApiResponse;
      const list = body.data?.legal_forms ?? body.legal_forms ?? [];
      cache.set(CACHE_KEY, { value: list, expires: Date.now() + CACHE_TTL_MS });
      if (mountedRef.current) setLegalForms(list);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      if (mountedRef.current) setError(e);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) void doFetch(false);
    return () => {
      mountedRef.current = false;
    };
  }, [enabled, doFetch]);

  const refetch = useCallback(async (): Promise<void> => {
    await doFetch(true);
  }, [doFetch]);

  return {
    legalForms,
    isLoading,
    isError: error !== null,
    error,
    refetch,
  };
}

/** Invalida manualmente la cache in-memory (utile post mutate admin). */
export function invalidateLegalFormsCache(): void {
  cache.clear();
}
